import { Component, forwardRef, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DTypeForm, DTypeTableField } from '@grupo-constant/next-dtype/form';
import { CodebarScannerWithSelect, DTypeCol, DTypeViewableCol, SelectFromUri, SelectOption, SelectWithGivenOptions } from '@grupo-constant/next-dtype/types';
import { TranslocoService } from '@ngneat/transloco';
import { cloneDeep } from 'lodash-es';
import { Subject, takeUntil } from 'rxjs';
import { TableRowDialogComponent } from '../../table-row.dialog.component';
import { DtypeItemComponent } from '../../../../dtype-item/dtype-item.component';
import { LocalizePipe } from '../../../../../pipes/standalone/localize.pipe';
import { DTypeFormFieldComponent } from '../../../../dtype-field.model';
import { FuseConfirmationService } from '../../../../../../../@fuse/services/confirmation/confirmation.service';
import { LocalizeService } from '../../../../../pipes/localize.service';


@Component({
    selector: 'tablewithfixedrows-component',
    templateUrl: './tablewithfixedrows.component.html',
    styles: [
        /* language=SCSS */
        `
            .error-panel {
                border-style: solid;
                border-width: 1px;
                border-color: red;
            }
        `],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [forwardRef(() => DtypeItemComponent), LocalizePipe]
})
export class DtypeTableWithFixedRowsComponent implements OnInit, OnDestroy, DTypeFormFieldComponent {

    @Input() field: DTypeTableField;
    @Input() isUpdate: boolean;
    @Input() useSummaryView: boolean;

    embeddedForms: DTypeForm[];

    options: SelectOption[] = [];

    total: any[];

    private unsubscribe = new Subject<void>();

    constructor(
        private _fuseConfirmationService: FuseConfirmationService,
        private localize: LocalizeService,
        private matDialog: MatDialog,
        private transloco: TranslocoService
    ) { }

    async ngOnInit() {
        const readonlyOnUpdate = (this.field.dTypeCol as DTypeViewableCol).readonlyOnUpdate;
        if (readonlyOnUpdate && this.isUpdate) {
            this.field.readonly = true;
        }
        this.embeddedForms = this.field.embeddedForms;
        if (this.field.uniqueIndex.length === 0) {
            this.warningDialog(this.transloco.translate('complexUniqueIndexNeeded', { caption: this.localize.transform((this.field.dTypeCol as DTypeViewableCol).caption) }));
            return;
        }
        this.calculateTotalSum();
        const selectField = this.field.table.dtype.column.find(c => c.name === this.field.uniqueIndex[0]) as SelectWithGivenOptions | SelectFromUri | CodebarScannerWithSelect;
        if (selectField) {
            this.embeddedForms.forEach((form, index) => {
                form.fields[selectField.name].hidden = true;
                if (!form.control.getRawValue()[selectField.name] && selectField.option.length > index && selectField.option[index].code) {
                    form.control.get(selectField.name)?.setValue(selectField.option[index].code);
                }
            });
            this.options = cloneDeep(selectField.option);
        }
        this.field.control.valueChanges
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(() => {
                this.calculateTotalSum();
            });

    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    someRowHasError() {
        for (const embeddedForms of this.field.embeddedForms) {
            if (!embeddedForms.control.valid && embeddedForms.control.touched) {
                return true;
            }
        }
        return false;
    }

    getColumnTotal(column: DTypeCol) {
        return this.total.find(t => t.name === column.name)?.total;
    }

    async editItem(row: DTypeForm) {
        const values = row.control.getRawValue();
        const title = this.options.find(opt => opt.code === values?.[this.field.uniqueIndex[0]])?.value;
        const dialog = this.matDialog.open(TableRowDialogComponent, {
            data: { dtype: this.field.table.dtype, values, title, fixedUniqueField: this.field.uniqueIndex[0], isUpdate: this.isUpdate },
            panelClass: 'table-row-dialog-panel',
            width: '90vw',
            height: '80vh',
            maxWidth: '1536px',
        });
        dialog.afterClosed().subscribe((updatedForm: DTypeForm) => {
            if (updatedForm) {
                row.control.patchValue(updatedForm.control.getRawValue());
            }
        });
    }

    private calculateTotalSum() {
        if (this.field.totalFields.length > 0) {
            this.total = [];
            this.field.totalFields.map((col) => {
                let tot = 0;
                for (const val of this.field.control.value) {
                    const valNum = val[col] === undefined ? 0 : val[col];
                    tot += valNum;
                }
                this.total.push({ name: col, total: tot });
            });
        }
    }

    private warningDialog(message: string) {
        this._fuseConfirmationService.open({
            title: this.transloco.translate('warning'),
            message: message,
            actions: {
                confirm: {
                    label: this.transloco.translate('accept')
                },
                cancel: {
                    label: this.transloco.translate('cancel'),
                    show: false
                }
            }
        });
    }
}


